import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../providers/NotificationProvider";
import DraggablePaper from "../../components/common/DraggablePaper";
import {
  fetchWorkerDetail,
  fetchWorkerIndicators,
  addWorkerGoal,
  updateWorkerGoal,
  deleteWorkerGoal,
} from "../../api/memberApi";

// ── 目標追加・編集ダイアログ ──────────────────────────────────────────
function GoalFormDialog({ open, onClose, onSaved, workerId, workerName, goal }) {
  const intl = useIntl();
  const notify = useNotification();
  const isEdit = Boolean(goal);

  const [indicators, setIndicators] = useState([]);
  const [loadingIndicators, setLoadingIndicators] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [plannedValue, setPlannedValue] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [deadline, setDeadline] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // 開いたとき初期化
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitting(false);

    if (isEdit) {
      // 編集モード：既存値をセット
      setSelectedIndicator(goal.indicator || null);
      setPlannedValue(goal.plannedValue || "");
      setPlannedDate(goal.plannedDate ? goal.plannedDate.slice(0, 10) : "");
      setDeadline(goal.deadline ? goal.deadline.slice(0, 10) : "");
      setIndicators(goal.indicator ? [goal.indicator] : []);
    } else {
      // 追加モード
      setSelectedIndicator(null);
      setPlannedValue("");
      setPlannedDate("");
      setDeadline("");
      // 指標一覧を取得
      setLoadingIndicators(true);
      fetchWorkerIndicators(workerId)
        .then((res) => {
          console.log("[WorkerGoalListPage] fetchWorkerIndicators raw response =", res);
          // バックエンドが配列を直接返す場合と、{ data: [...] } でラップする場合の両対応
          const list = Array.isArray(res) ? res : (res?.data ?? res ?? []);
          console.log("[WorkerGoalListPage] indicators list =", list);
          setIndicators(Array.isArray(list) ? list : []);
        })
        .catch((err) => {
          console.error("[WorkerGoalListPage] fetchWorkerIndicators error =", err);
          notify.apiError(err);
        })
        .finally(() => setLoadingIndicators(false));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const e = {};
    if (!isEdit && !selectedIndicator)
      e.indicator = intl.formatMessage({ id: "worker.goal.indicator.required" });
    if (!plannedValue.trim())
      e.plannedValue = intl.formatMessage({ id: "worker.goal.plannedValue.required" });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      if (isEdit) {
        await updateWorkerGoal({
          workerId,
          goal: {
            ...goal,
            plannedValue: plannedValue.trim(),
            plannedDate: plannedDate ? new Date(plannedDate).toISOString() : null,
            deadline: deadline ? new Date(deadline).toISOString() : null,
            updatedGoalAt: now,
          },
        });
        notify.success(intl.formatMessage({ id: "worker.goal.updateSuccess" }));
      } else {
        await addWorkerGoal({
          workerId,
          goal: {
            indicator: selectedIndicator,
            plannedValue: plannedValue.trim(),
            plannedDate: plannedDate ? new Date(plannedDate).toISOString() : null,
            deadline: deadline ? new Date(deadline).toISOString() : null,
            goalOwnerId: workerId,
            goalOwnerName: workerName,
            goalOwnerType: "Worker",
            createdGoalAt: now,
          },
        });
        notify.success(intl.formatMessage({ id: "worker.goal.createSuccess" }));
      }
      onSaved();
    } catch (err) {
      notify.apiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
      <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
        {intl.formatMessage({
          id: isEdit ? "worker.goal.edit.title" : "worker.goal.add.title",
        })}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        {/* 指標選択（追加時のみ） */}
        {!isEdit && (
          <FormControl fullWidth error={Boolean(errors.indicator)} disabled={loadingIndicators}>
            <InputLabel>{intl.formatMessage({ id: "worker.goal.indicator" })}</InputLabel>
            <Select
              value={selectedIndicator?.indicatorName || ""}
              label={intl.formatMessage({ id: "worker.goal.indicator" })}
              onChange={(e) => {
                const ind = indicators.find((i) => i.indicatorName === e.target.value);
                setSelectedIndicator(ind || null);
              }}
            >
              {indicators.map((ind, idx) => (
                <MenuItem key={idx} value={ind.indicatorName}>
                  {ind.indicatorName}
                  {ind.unit ? `（${ind.unit}）` : ""}
                </MenuItem>
              ))}
            </Select>
            {errors.indicator && <FormHelperText>{errors.indicator}</FormHelperText>}
          </FormControl>
        )}
        {/* 編集時は指標を読み取り表示 */}
        {isEdit && (
          <TextField
            label={intl.formatMessage({ id: "worker.goal.indicator" })}
            value={goal?.indicator?.indicatorName || ""}
            InputProps={{ readOnly: true }}
            fullWidth
            variant="outlined"
            size="small"
          />
        )}
        {/* 計画値 */}
        <TextField
          label={`${intl.formatMessage({ id: "worker.goal.plannedValue" })}${selectedIndicator?.unit || (goal?.indicator?.unit ? `（${goal.indicator.unit}）` : "")}`}
          value={plannedValue}
          onChange={(e) => setPlannedValue(e.target.value)}
          error={Boolean(errors.plannedValue)}
          helperText={errors.plannedValue}
          fullWidth
          size="small"
        />
        {/* 計画日 */}
        <TextField
          label={intl.formatMessage({ id: "worker.goal.plannedDate" })}
          type="date"
          value={plannedDate}
          onChange={(e) => setPlannedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
          size="small"
        />
        {/* 期限 */}
        <TextField
          label={intl.formatMessage({ id: "worker.goal.deadline" })}
          type="date"
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {intl.formatMessage({ id: "common.cancel" })}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loadingIndicators}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            intl.formatMessage({ id: isEdit ? "common.save" : "common.create" })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── メインページ ──────────────────────────────────────────────────────
export default function WorkerGoalListPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workerId } = useParams();
  const notify = useNotification();

  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);

  // ダイアログ
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = 追加、goal = 編集
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadWorker = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWorkerDetail(workerId);
      setWorker(data);
    } catch (err) {
      notify.apiError(err);
    } finally {
      setLoading(false);
    }
  }, [workerId, notify]);

  useEffect(() => {
    loadWorker();
  }, [loadWorker]);

  const goals = worker?.goals || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  // 削除実行
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteWorkerGoal({ workerId, goal: deleteTarget });
      notify.success(intl.formatMessage({ id: "worker.goal.deleteSuccess" }));
      setDeleteTarget(null);
      loadWorker();
    } catch (err) {
      notify.apiError(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/workers/${workerId}`)}
          variant="text"
        >
          {intl.formatMessage({ id: "common.back" })}
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: "worker.goal.list.title" })}
          </Typography>
          {worker?.workerName && (
            <Typography variant="body2" color="text.secondary">
              {worker.workerName}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditTarget(null); setFormOpen(true); }}
        >
          {intl.formatMessage({ id: "worker.goal.add.button" })}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : goals.length === 0 ? (
        <Typography color="text.secondary">
          {intl.formatMessage({ id: "worker.goal.noGoals" })}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "worker.goal.indicator" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  {intl.formatMessage({ id: "worker.goal.plannedValue" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "worker.goal.unit" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "worker.goal.plannedDate" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "worker.goal.deadline" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center" width={100}>
                  {intl.formatMessage({ id: "common.actions" })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {goals.map((goal, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {goal.indicator?.indicatorName || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{goal.plannedValue || "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {goal.indicator?.unit || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(goal.plannedDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(goal.deadline)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={intl.formatMessage({ id: "common.edit" })}>
                      <IconButton
                        size="small"
                        onClick={() => { setEditTarget(goal); setFormOpen(true); }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={intl.formatMessage({ id: "common.delete" })}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(goal)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 目標追加・編集ダイアログ */}
      <GoalFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); loadWorker(); }}
        workerId={workerId}
        workerName={worker?.workerName || ""}
        goal={editTarget}
      />

      {/* 削除確認ダイアログ */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
          {intl.formatMessage({ id: "worker.goal.deleteConfirm.title" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage({ id: "worker.goal.deleteConfirm.message" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {intl.formatMessage({ id: "common.cancel" })}
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              intl.formatMessage({ id: "common.delete" })
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
